"use client";

import Link from "next/link";
import { useState } from "react";
import { signIn } from "next-auth/react";
import { motion } from "motion/react";
import Image from "next/image";
import { Mail, ArrowLeft, Loader2 } from "lucide-react";
import { SiGoogle, SiInstagram } from "react-icons/si";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  async function handleGoogleSignIn() {
    setIsLoading(true);
    await signIn("google", { callbackUrl: "/dashboard" });
  }

  async function handleInstagramSignIn() {
    setIsLoading(true);
    await signIn("instagram", { callbackUrl: "/dashboard" });
  }

  async function handleEmailSignIn(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    await signIn("resend", { email, callbackUrl: "/dashboard" });
    setEmailSent(true);
    setIsLoading(false);
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-neon-purple/10 via-dark-surface to-neon-pink/5" />
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-neon-purple/20 rounded-full blur-[150px] -z-10" />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-neon-pink/15 rounded-full blur-[120px] -z-10" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <div className="glass-card-elevated p-8">
          <div className="flex items-center gap-3 mb-8">
            <Image src="/unicorn-icon.png" alt="Stashds" width={48} height={48} className="rounded-xl" />
            <div>
              <h1 className="text-2xl font-display font-bold">Welcome back</h1>
              <p className="text-white/50 text-sm">Sign in to Stashds</p>
            </div>
          </div>

          {emailSent ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="w-16 h-16 rounded-full bg-cyber-lime/20 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-cyber-lime" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Check your email</h2>
              <p className="text-white/60">
                We sent a magic link to <strong>{email}</strong>
              </p>
              <button
                onClick={() => setEmailSent(false)}
                className="mt-6 text-neon-purple hover:text-neon-pink transition-colors"
              >
                Use a different email
              </button>
            </motion.div>
          ) : (
            <>
              <button
                onClick={handleInstagramSignIn}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white font-medium hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <SiInstagram className="w-5 h-5" />
                    Continue with Instagram
                  </>
                )}
              </button>

              <button
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl bg-white text-dark-surface font-medium hover:bg-white/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-3"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <SiGoogle className="w-5 h-5" />
                    Continue with Google
                  </>
                )}
              </button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-dark-elevated text-white/40">
                    or continue with email
                  </span>
                </div>
              </div>

              <form onSubmit={handleEmailSignIn} className="space-y-4">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-white/70 mb-2"
                  >
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="input-glass"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading || !email}
                  className="w-full btn-neon disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Mail className="w-5 h-5 mr-2" />
                      Send magic link
                    </>
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-white/50">
                Don't have an account?{" "}
                <Link
                  href="/register"
                  className="text-neon-purple hover:text-neon-pink transition-colors"
                >
                  Sign up
                </Link>
              </p>
            </>
          )}
        </div>
      </motion.div>
    </main>
  );
}
